/**
 * AYUNEL 商品页「日本国内(名古屋)から発送の対象」提示行 —— blocks/ayunel-pdp-domestic-line.liquid 的行为层(新房 Horizon 4.1.4)。2026-09-13。
 *
 * 只管一件事:**什么时候能看见**。文案与判定全在 Liquid(服务端按选中变体渲染),这里不拼一个字。
 * 以 <script type="module"> 引入(与套装块同姿势),要 import 4.1.4 的标准事件名。
 *
 * 死约(每条都对应一个审出的坑,2026-09-13 Codex / 独立审查,含二轮):
 * - 收到 shopify:product:select 就**立即隐藏**(事件在请求发出前派发;请求在途 1–3 秒里绝不留上一个色码的句子)。
 * - 每次选择递增代号,只认最新一代的结果(快速连点时旧请求的结果不许盖掉新的)。
 * - 组合不存在(resource 为 null)/ 请求失败或被中止 / 新 HTML 里找不到自己或自己也是隐藏 → 保持隐藏,不 return 留旧内容。
 * - 只认同一商品(productId)的结果;推荐卡 / quick-add 里的选择事件不理。
 * - 挂载与返回键(pageshow)时核对**页面此刻应是哪个变体**:先看选项按钮(客人一点就变,请求在途也准 —— 每个选项自带
 *   data-variant-id),再看表单隐藏 input(响应回来才变;组合不存在时它是空串 = 也要清)。不一致 → 先清空,再按选项给的
 *   变体 id **自己向服务端补拉一次本 section**(Section Rendering API)换进来 —— 模块晚于客人换色时才有这条恢复路径
 *   (Codex 二轮高①②:只比表单会留下错色码的句子、空串被当成「无表单」放行)。补拉也走代号,新事件一来就作废。
 * - 宿主永远保留(空 + hidden),切回対象色码时才有人接事件。编辑器里首屏非対象时 Liquid 渲染占位说明,不做核对。
 * 守卫:tests/test_theme_pdp_domestic_line.py(node 假 DOM 跑本文件)。
 */
import { StandardEvents } from '@shopify/events';

const TAG = 'ayunel-pdp-domestic-line';

class AyunelPdpDomesticLine extends HTMLElement {
  #controller;
  #generation = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    window.addEventListener('pageshow', () => this.#verifyAgainstPage(), { signal });
    if (!this.hasAttribute('data-editor-placeholder')) this.#verifyAgainstPage();
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#controller?.abort();
  }

  #scope() {
    return this.closest('.shopify-section') || document;
  }

  /** 页面此刻应是哪个变体。返回 { known, id }:
   *  known=false = 页上找不到任何来源(不放行也不瞎猜:清空);known && id=null = 组合不存在 / 连续换了两项此刻不可知(清空、不补拉)。
   *  选项按钮上的 data-variant-id = 「选它、其它项按服务端渲染时的选择」会得到的变体:静止时各组相同;
   *  请求在途时只有被换的那一组不同于本行渲染时的变体 —— 那个就是目标。两组都不同 = 连换两项,不猜。 */
  #pageVariant() {
    const scope = this.#scope();
    const rendered = this.dataset.variantId || '';
    const picker = scope.querySelector(`variant-picker[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`)
      || scope.querySelector('variant-picker');
    const ids = [];
    if (picker) {
      for (const el of picker.querySelectorAll('input[type="radio"]:checked, select')) {
        const opt = el.tagName === 'SELECT' ? el.options[el.selectedIndex] : el;
        const id = opt && opt.dataset ? opt.dataset.variantId : undefined;
        if (id !== undefined) ids.push(String(id));
      }
    }
    if (ids.length) {
      if (ids.every((x) => x === ids[0])) return { known: true, id: ids[0] || null };
      const changed = ids.filter((x) => x !== rendered);
      return { known: true, id: changed.length === 1 ? changed[0] || null : null };
    }
    const input = scope.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]');
    if (input) return { known: true, id: input.value ? String(input.value) : null };
    return { known: false, id: null };
  }

  #verifyAgainstPage() {
    const { known, id } = this.#pageVariant();
    const rendered = this.dataset.variantId || '';
    if (known && id && rendered && id === rendered) return;
    const generation = ++this.#generation;
    this.#clear();
    if (known && id) this.#resync(id, generation);
  }

  /** 模块晚到 / 返回键后,按页面此刻的变体自己补拉一次本 section 的服务端渲染 */
  #resync(variantId, generation) {
    const section = this.closest('.shopify-section');
    const sectionId = section && section.id ? section.id.replace(/^shopify-section-/, '') : '';
    if (!sectionId) return;
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);
    fetch(url.toString(), { signal: this.#controller?.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((text) => {
        this.#adopt(new DOMParser().parseFromString(text, 'text/html'), generation);
      })
      .catch(() => {
        /* 失败 → 已清空,保持隐藏 */
      });
  }

  #clear() {
    this.replaceChildren();
    this.dataset.variantId = '';
    this.removeAttribute('data-editor-placeholder');
    this.hidden = true;
  }

  /** 从一份新 HTML 里取同 block id 的自己换进来(代号只在这里判一次:旧请求 / 旧补拉的结果一律作废)。
   *  新的自己也是隐藏 → 记下它的变体 id、保持隐藏;找不到 → 保持清空 */
  #adopt(html, generation) {
    if (generation !== this.#generation) return;
    const next = html.querySelector(`${TAG}[data-block-id="${CSS.escape(this.dataset.blockId || '')}"]`);
    if (!next) return;
    this.dataset.variantId = next.dataset.variantId || '';
    if (next.hasAttribute('hidden') || next.hasAttribute('data-editor-placeholder')) return; // hidden ⇔ 空(Liquid 保证,真渲染用例守着)
    this.replaceChildren(...Array.from(next.childNodes, (node) => document.importNode(node, true)));
    this.hidden = false;
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;
    // 事件自带商品 id(可能是 gid 形式),不是本页主商品的选择不理 —— 在清空之前判
    const pid = String((event.product && event.product.id) || '').split('/').pop();
    if (pid && pid !== this.dataset.productId) return;

    const generation = ++this.#generation;
    this.#clear();

    event.promise
      .then((result) => {
        const detail = result && result.detail;
        if (!detail || !detail.html || !detail.resource) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;
        this.#adopt(detail.html, generation);
      })
      .catch(() => {
        /* AbortError / 网络失败 → 已清空,保持隐藏 */
      });
  };
}

if (!customElements.get(TAG)) customElements.define(TAG, AyunelPdpDomesticLine);
